import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { ProductKind } from '@/common/product-kind.enum';
import { Component, DestroyRef, OnInit, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClassicEditor } from '@/common/editor/classic-editor';
import { Observable, map } from 'rxjs';
import { LoaderService } from 'src/app/common/loader/loader.service';
import { ProductApiService } from '../product.api.service';
import { DescriptionItem, DescriptionPayload } from '../product.model';
import { ProductService } from '../product.service';

Object.defineProperty(Array.prototype, 'mapDescriptionHeader', {
	value: function (use?: OfferTransportKind) {
		return (this as DescriptionPayload[]).map((item: DescriptionPayload) => ({
			descriptionHeader: item.descriptionHeader,
			description: item.description,
			use: use ?? item.use,
		}));
	},
});

interface DescriptionArray<T = unknown> extends Array<T> {
	mapDescriptionHeader(use?: OfferTransportKind): DescriptionPayload[];
}

@Component({
	selector: 'app-product-description',
	templateUrl: './product-description.component.html',
})
export class ProductDescriptionComponent implements OnInit {
	editable = input(true);
	use = input.required<OfferTransportKind>();
	productKind = input.required<ProductKind>();

	descriptions = signal<DescriptionItem[]>([]);
	editor: typeof ClassicEditor = ClassicEditor;
	editorConfig: { toolbar: string[] } = {
		toolbar: ['bold', 'italic', '|', 'bulletedList', 'numberedList'],
	};

	readonly OfferTransportKind = OfferTransportKind;

	private readonly service = inject(ProductService);
	private readonly loader = inject(LoaderService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly productApiService = inject(ProductApiService);

	ngOnInit(): void {
		this.getDescriptions();
		this.service.onChangeProductType.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.getDescriptions();
		});
	}

	updateDescription(item: DescriptionItem): void {
		this.service.data.descriptions = [
			...(this.service.data.descriptions as DescriptionArray<DescriptionPayload>)
				.mapDescriptionHeader()
				.filter(
					(description) =>
						description.descriptionHeader?.id !== item.descriptionHeader?.id ||
						description.use !== this.use(),
				),
			{ descriptionHeader: item.descriptionHeader, description: item.description, use: this.use() },
		];
	}

	copyDescriptions(): void {
		this.loader.show();

		const oppositeUse =
			this.use() === OfferTransportKind.COACH ? OfferTransportKind.PLANE : OfferTransportKind.COACH;

		this.getDescriptionRequirement(oppositeUse).subscribe((descriptions) => {
			this.descriptions.set(descriptions);
			const actualDescriptions = (
				this.service.data.descriptions as DescriptionArray<DescriptionPayload>
			).mapDescriptionHeader();

			this.service.data.descriptions = [
				...actualDescriptions.filter((description) => description.use !== this.use()),
				...(this.descriptions() as unknown as DescriptionArray<DescriptionPayload>).mapDescriptionHeader(
					this.use(),
				),
			];

			this.loader.hide();
		});
	}

	private getDescriptions(): void {
		if (this.productKind() === null) {
			return;
		}

		this.loader.show();

		this.getDescriptionRequirement(this.use()).subscribe((descriptions) => {
			this.descriptions.set(descriptions);
			this.loader.hide();
		});
	}

	private findDescription(descriptionId: number, use: OfferTransportKind): DescriptionPayload {
		return this.service.data.descriptions.find(
			(findObj) => findObj.descriptionHeader?.id == descriptionId && findObj.use === use,
		);
	}

	private getDescriptionRequirement(use: OfferTransportKind): Observable<DescriptionItem[]> {
		return this.productApiService.getProductDescriptionsRequirement(this.productKind()).pipe(
			map((result) =>
				result.results.map((obj) => {
					const findUniqueDescription = this.findDescription(obj.descriptionHeader.id, use);
					return {
						isRequired: obj.isRequired,
						comment: obj.descriptionHeader.comment,
						descriptionHeader: obj.descriptionHeader,
						description: findUniqueDescription ? findUniqueDescription?.description : '',
						descriptionHeaderTitle: obj.descriptionHeader.name,
						use: this.use(),
					};
				}),
			),
		);
	}
}
